import React from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

function StockAccumulation({ data, currency, exchangeRate }) {
  const { t } = useTranslation();

  if (!data) return null;

  const formatCurrency = (usd) => {
    if (usd == null) return '—';
    if (currency === 'NOK' && exchangeRate) {
      return `${(usd * exchangeRate).toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr`;
    }
    return `$${usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const { thisYear, allTime, currentPriceUSD } = data;
  const timeline = Array.isArray(data.timeline) ? data.timeline : (data.timeline?.$values || []);
  const currLabel = currency === 'NOK' ? 'kr' : '$';

  // Build chart data from timeline
  const chartData = timeline.map(point => ({
    date: new Date(point.date).toLocaleDateString('nb-NO', { month: 'short', year: '2-digit' }),
    shares: parseFloat(point.cumulativeShares?.toFixed(2) ?? 0),
    valueUSD: currentPriceUSD && point.cumulativeShares ? parseFloat((point.cumulativeShares * currentPriceUSD).toFixed(0)) : null
  }));

  const gainLossColor = allTime?.gainLossUSD >= 0 ? 'var(--success-500)' : 'var(--danger-500)';

  return (
    <div className="stock-accumulation">
      <h3>{t('compensation.stockAccumulation')}</h3>

      <div className="accumulation-panels">
        {/* This Year */}
        <div className="accumulation-panel">
          <h4>{t('compensation.thisYear')}</h4>
          <div className="panel-stats">
            <div className="stat">
              <span className="stat-label">{t('compensation.vestedShares')}</span>
              <span className="stat-value">{thisYear?.vestedShares?.toFixed(2)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">{t('compensation.esppShares')}</span>
              <span className="stat-value">{thisYear?.esppShares?.toFixed(2)}</span>
            </div>
            {thisYear?.sharesSold > 0 && (
              <div className="stat" style={{ color: 'var(--danger-500)' }}>
                <span className="stat-label">{t('compensation.sharesSold')}</span>
                <span className="stat-value">-{thisYear.sharesSold.toFixed(2)}</span>
              </div>
            )}
            <div className="stat highlight">
              <span className="stat-label">{t('compensation.totalShares')}</span>
              <span className="stat-value">{thisYear?.totalShares?.toFixed(2)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">{t('compensation.currentValue')}</span>
              <span className="stat-value">
                {formatCurrency(thisYear?.valueUSD)}
              </span>
            </div>
          </div>
        </div>

        {/* All Time */}
        <div className="accumulation-panel">
          <h4>{t('compensation.allTime')}</h4>
          <div className="panel-stats">
            {allTime?.totalSharesSold > 0 && (
              <div className="stat">
                <span className="stat-label">{t('compensation.totalAcquired')}</span>
                <span className="stat-value">{allTime?.totalAcquiredShares?.toFixed(2)}</span>
              </div>
            )}
            {allTime?.totalSharesSold > 0 && (
              <div className="stat" style={{ color: 'var(--danger-500)' }}>
                <span className="stat-label">{t('compensation.sharesSold')}</span>
                <span className="stat-value">-{allTime.totalSharesSold.toFixed(2)}</span>
              </div>
            )}
            <div className="stat highlight">
              <span className="stat-label">{t('compensation.totalShares')}</span>
              <span className="stat-value">{allTime?.totalShares?.toFixed(2)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">{t('compensation.costBasis')}</span>
              <span className="stat-value">{formatCurrency(allTime?.costBasisUSD)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">{t('compensation.currentValue')}</span>
              <span className="stat-value">{formatCurrency(allTime?.currentValueUSD)}</span>
            </div>
            <div className="stat" style={{ color: gainLossColor }}>
              <span className="stat-label">{t('compensation.gainLoss')}</span>
              <span className="stat-value">
                {formatCurrency(allTime?.gainLossUSD)}
                {allTime?.gainLossPercent != null && ` (${allTime.gainLossPercent > 0 ? '+' : ''}${allTime.gainLossPercent}%)`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Cumulative chart */}
      {chartData.length > 0 && (
        <div className="accumulation-chart">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-border)" />
              <XAxis dataKey="date" stroke="var(--text-muted)" />
              <YAxis yAxisId="shares" orientation="left" stroke="#3b82f6" />
              <YAxis yAxisId="value" orientation="right" stroke="#10b981"
                tickFormatter={v => `${currLabel}${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--surface-card)', border: '1px solid var(--surface-border)', borderRadius: '8px' }}
                labelStyle={{ color: 'var(--text-primary)' }}
              />
              <Legend />
              <Line yAxisId="shares" type="monotone" dataKey="shares" name={t('compensation.shares')}
                stroke="#3b82f6" strokeWidth={2} dot={false} />
              {currentPriceUSD && (
                <Line yAxisId="value" type="monotone" dataKey="valueUSD" name={`${t('compensation.currentValue')} (${currLabel})`}
                  stroke="#10b981" strokeWidth={2} dot={false} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default StockAccumulation;
